import type { JSX } from 'react'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations'
import { Button } from 'core-components/library/button'
import type { IModalContent } from 'core-components/library/modalDialog/models/IModalContent'
import type { FinancialMetricMode } from 'partner360/entities/financialMetricMode'
import type { FinData } from 'partner360/entities/pbmDashboard'

import type { SobTableEntry } from '../../../model/types'
import { useSobActualsModalContent } from '../../../model/useSobActualsModalContent'
import { sobActualsModalTranslation } from '../../../translation'
import { SobActualsHierarchy } from '../sobActualsHierarchy/SobActualsHierarchy'
import { SobActualsIncompletePrompt } from '../sobActualsIncompletePrompt/SobActualsIncompletePrompt'
import { SobActualsRecalcPrompt } from '../sobActualsRecalcPrompt/SobActualsRecalcPrompt'
import type { SobTableTitlePlacement } from '../sobActualsTable/SobActualsTable'

import { SobActualsEntryTable } from './SobActualsEntryTable'
import { SobActualsModalHeader } from './SobActualsModalHeader'

import styles from './SobActualsModalContent.module.scss'

export interface SobActualsModalContentProps extends IModalContent {
  pid: string
  offering: string
  region: string
  country: string
  partnerName: string
  locationId: string
  category: string | null
  isBySubCategory: boolean
  isNoPlanEditable?: boolean
  defaultMetricMode: FinancialMetricMode
  availableMetricModes: FinancialMetricMode[]
  currentFiscalYear: number
  finData: FinData[]
  onConfirmed?: () => void | Promise<void>
}

export type SobActualsModalContentOwnProps = Omit<SobActualsModalContentProps, keyof IModalContent>

export const SobActualsModalContent = ({
  pid,
  offering,
  region,
  country,
  partnerName,
  locationId,
  category,
  isBySubCategory,
  isNoPlanEditable,
  defaultMetricMode,
  availableMetricModes,
  currentFiscalYear,
  finData,
  onConfirmed,
  closeModal,
}: SobActualsModalContentProps): JSX.Element => {
  const viewModel = useSobActualsModalContent({
    pid,
    offering,
    region,
    country,
    partnerName,
    locationId,
    category,
    isBySubCategory,
    isNoPlanEditable,
    defaultMetricMode,
    availableMetricModes,
    currentFiscalYear,
    finData,
    onConfirmed,
    closeModal,
  })
  const renderTable = (
    entry: SobTableEntry,
    title?: string,
    titlePlacement?: SobTableTitlePlacement
  ) => (
    <SobActualsEntryTable
      entry={entry}
      controller={viewModel.tableController}
      title={title}
      titlePlacement={titlePlacement}
    />
  )

  return (
    <div className={styles.root}>
      <SobActualsModalHeader
        pid={pid}
        region={region}
        country={country}
        partnerName={partnerName}
        locationId={locationId}
        availableMetricModes={availableMetricModes}
        resolvedMetricMode={viewModel.resolvedMetricMode}
        showOverall={viewModel.showOverall}
        shouldShowSobDetails={viewModel.shouldShowSobDetails}
        isSobDetailsMandatory={viewModel.isSobDetailsMandatory}
        onShowOverallChange={viewModel.setShowOverall}
        onShowSobDetailsChange={viewModel.setShowSobDetails}
        onMetricModeChange={viewModel.setMetricMode}
      />
      <div className={styles.content}>
        {viewModel.shouldShowSettingsUnavailableWarning && (
          <div className={styles.settingsWarning}>
            <InfoBlock
              type={InfoBlockType.Warning}
              text={sobActualsModalTranslation.settingsUnavailableWarning}
            />
          </div>
        )}
        {viewModel.isWaitingForSobSettings ? (
          <div className={styles.loadingShell}>
            <LoaderOverlay isVisible color="white" />
          </div>
        ) : viewModel.hasSubCategoryEntries ? (
          viewModel.tableEntryCategoryGroups.map((group) => {
            const recalcPromptMode = group.overallTableEntry
              ? viewModel.getCategoryRecalcPrompt(group.category)
              : null

            return (
              <div key={group.category} className={styles.categoryGroup}>
                {recalcPromptMode && (
                  <SobActualsRecalcPrompt
                    mode={recalcPromptMode}
                    onRecalculate={() => viewModel.handleRecalculateCategory(group.category)}
                    onCancelRecalculate={() => viewModel.handleCancelCategoryRecalc(group.category)}
                    onResetChanges={() => viewModel.handleResetCategoryChanges(group.category)}
                  />
                )}
                <SobActualsHierarchy
                  overallTableEntry={group.overallTableEntry}
                  subCategoryTableEntries={group.subCategoryTableEntries}
                  renderTable={renderTable}
                />
              </div>
            )
          })
        ) : (
          viewModel.tableEntries.map((entry) => <div key={entry.key}>{renderTable(entry)}</div>)
        )}
        {viewModel.hasApplySobActualsReviewError && (
          <div className={styles.errorMessage}>Unable to save SOB actuals. Please try again.</div>
        )}
        <div className={styles.actions}>
          <Button
            className={styles.actionButton}
            color="danger"
            variant="text"
            size="sm"
            disabled={viewModel.isApplyingSobActualsReview}
            onClick={closeModal}
          >
            <Translation text={sobActualsModalTranslation.cancel} span />
          </Button>
          <Button
            className={styles.actionButton}
            color="primary"
            size="sm"
            loading={viewModel.isApplyingSobActualsReview}
            disabled={viewModel.isWaitingForSobSettings}
            onClick={viewModel.handleSaveClick}
          >
            <Translation text={sobActualsModalTranslation.save} span />
          </Button>
        </div>
        <SobActualsIncompletePrompt
          show={viewModel.showIncompleteWarning}
          onReturnToFilling={viewModel.handleReturnToFilling}
          onSaveAsIs={viewModel.handleSaveAsIs}
          isSaving={viewModel.isApplyingSobActualsReview}
        />
      </div>
    </div>
  )
}
